
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { analyzeArticle } from '@/lib/articleAnalysis.functions';
import { toast } from 'sonner';

export interface TLDRAnalysis {
  tldr: string;
  keyPoints: string[];
  simplified: string;
  challengeThis: string;
}

export interface SentimentAnalysis {
  sentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  confidence: number;
  sentimentDrivers: string[];
  marketImplications: string;
  emotionalTone: string;
}

export const useArticleAI = () => {
  const [tldrData, setTldrData] = useState<TLDRAnalysis | null>(null);
  const [sentimentData, setSentimentData] = useState<SentimentAnalysis | null>(null);
  const [loadingTldr, setLoadingTldr] = useState(false);
  const [loadingSentiment, setLoadingSentiment] = useState(false);

  const generateAnalysis = useCallback(async (
    articleId: string, 
    analysisType: 'tldr' | 'sentiment',
    languageCode: string = 'en'
  ) => {
    const isLoadingTldr = analysisType === 'tldr';
    const setLoading = isLoadingTldr ? setLoadingTldr : setLoadingSentiment;
    const setData = isLoadingTldr ? setTldrData : setSentimentData;

    setLoading(true);
    try {
      const data = await analyzeArticle({
        data: { articleId, analysisType, languageCode },
      });

      if (data?.success && data?.analysis) {
        setData(data.analysis as never);
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error('Failed to analyze article');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      console.error(`Error generating ${analysisType} analysis:`, error);
      const lower = message.toLowerCase();
      toast.error(
        lower.includes('rate limit') || lower.includes('busy') || lower.includes('capacity')
          ? 'AI is busy right now, try again in a minute.'
          : message || `Failed to generate ${analysisType} analysis`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const autoAnalyzeArticle = useCallback(async (articleId: string, languageCode: string = 'en') => {
    // Reset state for new article
    setTldrData(null);
    setSentimentData(null);

    try {
      // Single optimized query to fetch existing analysis
      const tldrKey = languageCode === 'en' ? 'tldr' : `tldr_${languageCode}`;
      const sentimentKey = languageCode === 'en' ? 'sentiment' : `sentiment_${languageCode}`;
      
      const { data: existingAnalysis, error } = await supabase
        .from('article_analysis')
        .select('analysis_type, analysis_data')
        .eq('article_id', articleId)
        .in('analysis_type', [tldrKey, sentimentKey]);

      if (error) {
        console.error('Error fetching existing analysis:', error);
      }

      // Set existing data immediately for instant display
      existingAnalysis?.forEach((analysis) => {
        if (analysis.analysis_type === tldrKey) {
          setTldrData(analysis.analysis_data as unknown as TLDRAnalysis);
        } else if (analysis.analysis_type === sentimentKey) {
          setSentimentData(analysis.analysis_data as unknown as SentimentAnalysis);
        }
      });

      // Only generate missing analysis
      const hasExistingTldr = existingAnalysis?.some(a => a.analysis_type === tldrKey);
      const hasExistingSentiment = existingAnalysis?.some(a => a.analysis_type === sentimentKey);

      if (!hasExistingTldr) {
        await generateAnalysis(articleId, 'tldr', languageCode);
      }

      if (!hasExistingSentiment) {
        // small gap so the two model calls don't hit the rate limit together
        if (!hasExistingTldr) await new Promise((r) => setTimeout(r, 1200));
        await generateAnalysis(articleId, 'sentiment', languageCode);
      }
    } catch (error) {
      console.error('Error in autoAnalyzeArticle:', error);
    }
  }, [generateAnalysis]);

  const reset = useCallback(() => {
    setTldrData(null);
    setSentimentData(null);
  }, []);

  return {
    tldrData,
    sentimentData,
    loadingTldr,
    loadingSentiment,
    autoAnalyzeArticle,
    generateAnalysis,
    reset
  };
};
